import {
  type CalculationInfoResponse,
  ContractLoanTypeEnum,
} from '@kk/api/modules/customerPortal';
import { formatDate } from '@kk/shared/utils/date';
import { capitalizeFirstLetter } from '@kk/shared/utils/strings';
import { useLoanInfoRows } from '@kk/ui/hooks/useLoanInfoRows';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

export interface LoanInfoProps {
  calculation: CalculationInfoResponse;
}

export function LoanInfo(props: LoanInfoProps) {
  const { t } = useTranslation('ui');
  const { calculation } = props;

  const infoRows = useLoanInfoRows(calculation, {
    omitNotRelevantRows: true,
  }).filter(({ id }) => {
    if (calculation.loanProduct === ContractLoanTypeEnum.KKfast) {
      // "Rentefrekvens" should not be shown for KKfast in calculation results, but it is still shown in the comparison modal
      return id !== 'interestPaymentFrequency';
    }
    return true;
  });

  if (calculation.loanProduct === ContractLoanTypeEnum.Byggekredit) {
    infoRows.push({
      label: t('loan.firstInterestDate'),
      id: 'firstInterestDate',
      value: formatDate(calculation.firstInterestDate) ?? t('unavailable'),
    });
  }

  return (
    <div className="screen:bg-blue-5 screen:p-6 rounded-md screen:border border-blue-10 grid gap-y-6 content-start h-full">
      <h5 className="typ-headline-md">{t('loan.loanInformation')}</h5>
      <dl className="grid grid-cols-2 gap-y-3 typ-body-sm items-baseline">
        {infoRows.map(({ id, label, value }) => (
          <Fragment key={label}>
            <dt data-testid={`${id}-label`}>{capitalizeFirstLetter(label)}</dt>
            <dd className="place-self-end" data-testid={`${id}-value`}>
              {value}
            </dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}
